import TextField from '@material-ui/core/TextField';
import { MobileTimePicker } from '@material-ui/lab';
import moment from 'moment';
import React, { useState } from 'react';
import './App.css';
import AlcoholConfigControlWrapper from './components/AlcoholConfigControlWrapper';
import Counter from './components/Counter';
import ElementorButton from './components/ElementorButton';
import { Alcohols } from './constants/Alcohols';
import { Gender } from './constants/Gender';
import { TextDomain } from './constants/TextDomain';
import useDrinks from './hooks/useDrinks';
import { AlcoholType } from './types/AlcoholType';
import { alcoholsToAlcoholGrams, getActualAlcoholGrams } from './utils/alcoholGrams';
import { getPromileFromGrams } from './utils/promile';
import { getTimeToRide, getTimeToSoberMinutes } from './utils/times';

const { __ } = window.wp.i18n;

function App() {
    const [age, setAge] = useState<number>(18);
    const [weight, setWeight] = useState<number>(40);
    const [gender, setGender] = useState<Gender>(Gender.Male);
    const [computeClicked, setComputeClicked] = useState(false);

    const [drinkFrom, setDrinkFrom] = useState<moment.Moment>(moment().subtract(1, 'hour'));
    const [selectedTime, setSelectedTime] = useState<moment.Moment>(moment().subtract(1, 'hour'));

    // const [drinkTill, setDrinkTill] = useState<moment.Moment>(moment());
    const [drinks, dispatch] = useDrinks();

    const [drunkStatus, setDrunkStatus] = useState({ promile: 0, timeToSober: 0, timeToRide: 0 });

    const handleCompute = () => {
        setComputeClicked(true);

        const alcoholsCount = Object.keys(drinks).map((drink) => ({ type: drink as AlcoholType, count: drinks[drink as AlcoholType] }));
        const grams = alcoholsToAlcoholGrams(alcoholsCount);
        const gramsNow = getActualAlcoholGrams(weight, drinkFrom, grams);

        const timeToSober = getTimeToSoberMinutes(weight, grams);
        const promile = getPromileFromGrams(gender, weight, age, gramsNow);
        const timeToRide = getTimeToRide(timeToSober);

        setDrunkStatus({
            promile,
            timeToRide,
            timeToSober,
        });

        if (window.ga) {
            window.ga('set', 'page', document.location.pathname + '?result=true');
            window.ga('send', 'pageview');
        }
    };

    const handleTimeFromChange = (time: moment.Moment | null) => {
        if (time) {
            setSelectedTime(time);
        }
    };

    const handleAcceptDateChange = () => {
        setDrinkFrom(selectedTime);
    };

    const getResultDetailSection = (type: string) => {
        return (
            <div className="section-spacer">
                <div className="info-block" style={{ marginBottom: '0px' }}>
                    <div className="result-title top-title">{__(type + '_title', TextDomain)}</div>
                </div>
                <p className="small-text">{__(type + '_text', TextDomain)}</p>
                <p className="result-warning text">
                    <div className="result-warning-title small-title">{__('warning', TextDomain)}</div>
                    <div className="small-text">{__(type + '_warning', TextDomain)}</div>
                </p>
                {__(type + '_tip', TextDomain) !== type + '_tip' && (
                    <p className="result-tip text">
                        <div className="result-tip-title small-title">{__('Tip', TextDomain)}</div>
                        <div className="small-text">{__(type + '_tip', TextDomain)}</div>
                    </p>
                )}
            </div>
        );
    };

    return (
        <div>
            <div className="section-container">
                <div className="section-spacer header">
                    <h2 className="top-title">{__('Compute per mille', TextDomain)}</h2>
                </div>
                <div className="section-spacer">
                    <AlcoholConfigControlWrapper label={__('Your gender', TextDomain)}>
                        <div className="alcohol-config-control">
                            <div className="tt-form-control">
                                <select value={gender} onChange={(event) => setGender(event.target.value as Gender)}>
                                    <option value={Gender.Male}>{__('Male', TextDomain)}</option>
                                    <option value={Gender.Female}>{__('Female', TextDomain)}</option>
                                </select>
                            </div>
                        </div>
                    </AlcoholConfigControlWrapper>
                    <AlcoholConfigControlWrapper label={__('Your age', TextDomain)}>
                        <div className="alcohol-config-control">
                            <div className="tt-form-control">
                                <input type="number" min={18} max={70} value={age} onChange={(event) => setAge(parseInt(event.target.value))} />
                            </div>
                            <div className="tt-form-control-addon">
                                <span className="input-addon text">{__('y.o.', TextDomain)}</span>
                            </div>
                        </div>
                    </AlcoholConfigControlWrapper>
                    <AlcoholConfigControlWrapper label={__('Your weight', TextDomain)}>
                        <div className="alcohol-config-control">
                            <div className="tt-form-control">
                                <input
                                    type="number"
                                    min={40}
                                    max={150}
                                    value={weight}
                                    onChange={(event) => setWeight(parseInt(event.target.value))}
                                />
                            </div>
                            <div className="tt-form-control-addon">
                                <span className="input-addon text">kg</span>
                            </div>
                        </div>
                    </AlcoholConfigControlWrapper>
                    <AlcoholConfigControlWrapper label={__('First drink', TextDomain)}>
                        <div className="alcohol-config-control">
                            <MobileTimePicker
                                value={selectedTime}
                                onChange={handleTimeFromChange}
                                onAccept={handleAcceptDateChange}
                                ampm={false}
                                toolbarTitle={null}
                                okText={__('Ok', TextDomain)}
                                cancelText={__('Cancel', TextDomain)}
                                renderInput={(props) => (
                                    <TextField
                                        {...props}
                                        helperText=""
                                        className="tt-form-control"
                                        variant="standard"
                                        InputProps={{ disableUnderline: true }}
                                    />
                                )}
                            />
                            <div className="tt-form-control-addon">
                                <span className="input-addon text">{__('time', TextDomain)}</span>
                            </div>
                        </div>
                    </AlcoholConfigControlWrapper>
                    {Object.keys(Alcohols).map((alc) => (
                        <AlcoholConfigControlWrapper
                            key={alc}
                            label={__(Alcohols[alc as AlcoholType].label, TextDomain)}
                            icon={Alcohols[alc as AlcoholType].icon}>
                            <Counter
                                className="alcohol-config-control"
                                onDecrease={() => dispatch({ type: 'remove', alcohol: alc as AlcoholType })}
                                onIncrease={() => dispatch({ type: 'add', alcohol: alc as AlcoholType })}
                                value={drinks[alc as AlcoholType]}
                            />
                        </AlcoholConfigControlWrapper>
                    ))}
                </div>
            </div>

            <ElementorButton className="compute-btn" onClick={handleCompute}>
                {__('Compute', TextDomain)}
            </ElementorButton>
            <div className="result-container">
                {!computeClicked ? (
                    <div className="section-spacer">{__('You will see the result here.', TextDomain)}</div>
                ) : (
                    <>
                        <div className="section-spacer">
                            <div className="info-block">
                                <div className="result-title text">{__('Your actual alcohol per mille is:', TextDomain)}</div>
                                <div className="top-title">{drunkStatus.promile.toFixed(3).replace('.', ',')} &permil;</div>
                            </div>
                            {drunkStatus.promile > 0 && (
                                <>
                                    <div className="info-block">
                                        <div className="small-title">{__('You will be sober at:', TextDomain)}</div>
                                        <div className="small-text">
                                            {moment(drinkFrom).add(drunkStatus.timeToSober, 'minute').format('LL')} /{' '}
                                            <b>{moment(drinkFrom).add(drunkStatus.timeToSober, 'minute').format('LT')}</b>
                                        </div>
                                    </div>
                                    <div className="info-block">
                                        <div className="small-title">{__('You can safely ride a car at:', TextDomain)}</div>
                                        <div className="small-text">
                                            {moment(drinkFrom).add(drunkStatus.timeToRide, 'hour').format('LL')} /{' '}
                                            <b>{moment(drinkFrom).add(drunkStatus.timeToRide, 'hour').format('LT')}</b>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {drunkStatus.promile > 0 && drunkStatus.promile <= 0.25 && getResultDetailSection('mild_intoxication')}
                        {drunkStatus.promile > 0.25 && drunkStatus.promile <= 0.5 && getResultDetailSection('good_mood')}
                        {drunkStatus.promile > 0.5 && drunkStatus.promile <= 0.85 && getResultDetailSection('slight_intoxication')}
                        {drunkStatus.promile > 0.85 && drunkStatus.promile <= 1.6 && getResultDetailSection('intoxication')}
                        {drunkStatus.promile > 1.6 && drunkStatus.promile <= 2.7 && getResultDetailSection('severe_state_of_intoxication')}
                        {drunkStatus.promile > 2.7 && drunkStatus.promile <= 3.6 && getResultDetailSection('unconsciousness')}
                        {drunkStatus.promile > 3.6 && getResultDetailSection('alcohol_poisoning')}
                    </>
                )}
            </div>
            <div>
                <a
                    className="small-text app-link"
                    href="https://play.google.com/store/apps/details?id=com.ppas.promileInfo"
                    target="_blank"
                    rel="noreferrer">
                    {__('Mobile app Alculator is available for free', TextDomain)}
                </a>
            </div>
            <div className="googlePlayLogoWrapper">
                <div className="googlePlayLogo">
                    <a href="https://play.google.com/store/apps/details?id=com.ppas.promileInfo">
                        <img src={window.alculatorGlobals.imagesDir + '/google-play-badge.png'}></img>
                    </a>
                </div>
            </div>
        </div>
    );
}

export default App;
